
import { useRef, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Tables } from "@/integrations/supabase/types";
import { getGoogleMarkerIcon, generateInfoWindowContent } from "@/components/maps/markers/CrimeMarkerStyles";

type CrimeReport = Tables<"crime_reports">;

interface UseCrimeMarkersProps {
  map: google.maps.Map | null;
  crimeReports: CrimeReport[];
  showLast24Hours: boolean;
}

export const useCrimeMarkers = ({ map, crimeReports, showLast24Hours }: UseCrimeMarkersProps) => {
  const markers = useRef<google.maps.Marker[]>([]);
  const infoWindows = useRef<google.maps.InfoWindow[]>([]);
  const { toast } = useToast();
  
  const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

  useEffect(() => {
    if (!map) return;

    // Clear existing info windows
    infoWindows.current.forEach(infoWindow => infoWindow.close());
    infoWindows.current = [];

    // Clear existing markers
    markers.current.forEach(marker => marker.setMap(null));
    markers.current = [];

    try {
      if (crimeReports.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        
        crimeReports.forEach((report) => {
          const position = new google.maps.LatLng(report.latitude, report.longitude);
          
          const reportDate = new Date(report.created_at);
          const isRecent = reportDate > twentyFourHoursAgo;
          
          if (showLast24Hours && !isRecent) {
            return;
          }
          
          bounds.extend(position);
          
          try {
            const marker = new google.maps.Marker({
              position,
              map,
              title: report.category,
              icon: getGoogleMarkerIcon(report.category, isRecent),
              animation: isRecent ? google.maps.Animation.DROP : null,
              zIndex: isRecent ? 2 : 1
            });
            
            const infoContent = generateInfoWindowContent(
              report.category,
              report.created_at,
              report.location_name,
              report.latitude,
              report.longitude,
              report.description,
              report.image_paths,
              isRecent
            );
            
            const infoWindow = new google.maps.InfoWindow({
              content: infoContent,
              maxWidth: 300
            });
            
            infoWindows.current.push(infoWindow);
            
            marker.addListener("click", () => {
              infoWindows.current.forEach(window => window.close());
              infoWindow.open(map, marker);
            });
            
            markers.current.push(marker);
          } catch (markerError) {
            console.error("Error creating marker:", markerError);
          }
        });
      }
    } catch (err) {
      console.error("Error creating markers:", err);
      toast({
        title: "Map Error",
        description: "Failed to create crime markers on the map.",
        variant: "destructive"
      });
    }

    return () => {
      markers.current.forEach(marker => marker.setMap(null));
      infoWindows.current.forEach(window => window.close());
    };
  }, [map, crimeReports, showLast24Hours, twentyFourHoursAgo, toast]);

  const hasRecentReports = () => {
    return crimeReports.some(r => new Date(r.created_at) > twentyFourHoursAgo);
  };

  return { hasRecentReports };
};
